import {
  Component,
  OnDestroy,
  OnInit,
  CUSTOM_ELEMENTS_SCHEMA,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ImagePlayerOpenOptions, ImagePlayerService } from '../../../core/services/image-player.service';
import { StorageService } from '../../../core/services/storage.service';
import { NotificationService } from '../../../core/services/notification.service';

@Component({
  selector: 'app-image-player-modal',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './image-player-modal.component.html',
  styleUrls: ['./image-player-modal.component.css'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ImagePlayerModalComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly imagePlayerService = inject(ImagePlayerService);
  private readonly storageService = inject(StorageService);
  private readonly notificationService = inject(NotificationService);
  private readonly translateService = inject(TranslateService);

  open = false;
  title = '';
  imageUrl: string | null = null;
  loading = false;

  ngOnInit(): void {
    this.imagePlayerService.request$
      .pipe(takeUntil(this.destroy$))
      .subscribe((request) => {
        if (!request) {
          this.reset();
          return;
        }
        this.loadRequest(request);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onOverlayClick(): void {
    this.close();
  }

  close(): void {
    this.imagePlayerService.close();
  }

  private loadRequest(request: ImagePlayerOpenOptions): void {
    this.open = true;
    this.title = request.title;
    this.imageUrl = null;
    this.loading = true;

    if (request.imageUrl) {
      this.imageUrl = request.imageUrl;
      this.loading = false;
      return;
    }

    if (!request.s3Key) {
      this.handleLoadError();
      return;
    }

    this.storageService
      .generateDownloadUrl(request.s3Key, { inline: true })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success && response.data?.downloadUrl) {
            this.imageUrl = response.data.downloadUrl;
          } else {
            this.handleLoadError();
          }
          this.loading = false;
        },
        error: () => {
          this.handleLoadError();
          this.loading = false;
        }
      });
  }

  private handleLoadError(): void {
    this.reset();
    this.notificationService.error(
      this.translateService.instant('dashboard.common.error'),
      this.translateService.instant('dashboard.common.imagePlayer.loadError')
    );
  }

  private reset(): void {
    this.open = false;
    this.title = '';
    this.imageUrl = null;
    this.loading = false;
  }
}
